import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { EventService, WindowRef } from '@spartacus/core'
import { DownloadEvent } from '../gtm-event/download-event.events'
import { ChatBotService } from '../../../services/chat-bot/chat-bot.service'
import { ClickEvent } from '../gtm-event/click-event.events'
import { MbNavigationService } from '../../../navigation/mb-navigation.service'
import { CmsLinkType } from '../../../enums/cms-link-type'
import { CmsProductService } from '../../../services/cms-product/cms-product.service'
import { Router } from '@angular/router'
import { MbEventListenerService } from '../../../services/mb-event-listener/event-listener.service'
import { ProductResolvedEvent } from '../events/product-resolved-event'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { LinkData } from '../../types'

@Component({
  selector: 'mb-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LinkComponent implements OnInit {

  data$ = new BehaviorSubject<LinkData>({})
  url$ = new BehaviorSubject<string | string[] | null>('')
  anchor$ = new BehaviorSubject<string | null>('')
  dataCy$ = new BehaviorSubject<string>('')
  cmsLinkType$ = new BehaviorSubject<string>('')
  text$ = new BehaviorSubject<string>('')

  @Output() clickEvent = new EventEmitter()

  @Input() ariaExpanded?: boolean | undefined | null
  @Input() ariaControls?: string | undefined | null
  @Input() dataEventCategory: string | undefined | null
  @Input() dataEventAction: string | undefined | null
  @Input() dataEventLabel: string | undefined | null
  @Input() dataEventType: string | undefined | null
  @Input() dataFilter: string | undefined | null
  @Input() dataToggle: string | undefined | null
  @Input() dataTarget: string | undefined | null
  @Input() dataTitle: string | undefined | null
  @Input() dataFooter: string | undefined | null
  @Input() dataColor: string | undefined | null
  @Input() fragment: string | undefined | null
  @Input() isCxRoute: boolean | undefined | null
  @Input() id: string | undefined | null
  @Input() style: any | undefined | null
  @Input() previewId: string | undefined | null
  @Input() queryParams: string | undefined | null
  @Input() styleClass: string | string[] | undefined | null
  @Input() tabindex: number | string | undefined | null
  @Input() useSlot: boolean | undefined | null

  @Input() set dataCy (dataCy: string | undefined | null) {
    this.dataCy$.next(dataCy ?? '')
  }

  @Input() set linkData (data: LinkData | undefined | null) {
    this.data$.next(data ?? {})
  }

  trigger$ = this.data$.pipe(
    tap(data => this.cmsLinkType$.next(data.uid ?? CmsLinkType.CONTENT_LINK)),
    tap(data => this.anchor$.next(data.anchor || null)),
    switchMap(data => this.getUrlByCmsLinkType(data)),
    tap(url => this.url$.next(url ?? '')))

  state$ = combineLatest([this.data$, this.url$, this.anchor$, this.dataCy$, this.cmsLinkType$, this.text$]).pipe(
    map(([data, url, anchor, dataCy, cmsLinkType, text]) => ({ data, url, anchor, dataCy, cmsLinkType: cmsLinkType, text })))

  constructor (private events: EventService,
               protected winRef: WindowRef,
               private chatBotService: ChatBotService,
               private navigationService: MbNavigationService,
               private cmsProductService: CmsProductService,
               private router: Router,
               private eventService: MbEventListenerService) {
  }

  getUrlByCmsLinkType (data: LinkData = {}): Observable<string | string[] | null> {
    const cmsLinkType = data.uid ?? CmsLinkType.CONTENT_LINK
    let url = data.url ?? ''
    switch (cmsLinkType) {
      case CmsLinkType.PHONE_LINK: {
        url = url as string
        return of(`tel: ${url.replace('tel:', '')}`)
      }
      case CmsLinkType.COOKIE_SETTINGS:
      case CmsLinkType.PRINT:
      case CmsLinkType.SPECIAL:
      case CmsLinkType.CHATBOT_LINK: {
        return of(null)
      }
      case CmsLinkType.EMAIL_LINK:
      case CmsLinkType.EMAIL_LINK_BUTTON: {
        url = url as string
        return of(`mailto:${url.replace('mailto:', '')}`)
      }
      case CmsLinkType.ANCHOR:
      case CmsLinkType.ANCHOR_BUTTON: {
        return of([])
      }
      case CmsLinkType.URL:
      case CmsLinkType.URL_BUTTON:
      case CmsLinkType.DOCUMENT_LINK:
      case CmsLinkType.DOCUMENT_LINK_BUTTON: {
        url = url as string
        url = url.startsWith('www') ? `http://${url}` : url
        return of(!!data.anchor ? `${url}#${data.anchor}` : url)
      }
      case CmsLinkType.RELATED_PRODUCT_LINK:
      case CmsLinkType.PRODUCT_LINK:
      case CmsLinkType.PRODUCT_LINK_BUTTON:
      case CmsLinkType.CONTENT_LINK:
      case CmsLinkType.CONTENT_LINK_BUTTON: {
        if (!data.pageUid && !data.productCode && !data.url && !data.anchor) {
          return of('/not-found')
        }
        if (Array.isArray(data.url)) {
          return of(data.url)
        }
        if (!this.isCxRoute && !data.pageUid && !data.productCode && data.anchor) {
          url = (url as string).startsWith('/') ? url : `/${url}`
          return of(url)
        }
        if (data.productCode) {
          this.cmsProductService.batchProductRequests(data.productCode)
          return this.retrieveProductEventUrl()
        }
        if (data.pageUid) {
          return of(`/${this.navigationService.getSeoUrlOfPageRef(data.pageUid)}`)
        }
        url = url ?? '/not-found'
        if (this.isCxRoute) {
          return of(url)
        }
        return of((url as string).startsWith('/') ? url : `/${url}`)
      }
      default: {
        return of('/not-found')
      }
    }
  }

  onClick (event: Event): void {
    const cmsLinkType = this.cmsLinkType$.value
    if (cmsLinkType === CmsLinkType.CHATBOT_LINK
      || cmsLinkType === CmsLinkType.PRINT
      || cmsLinkType === CmsLinkType.SPECIAL
      || cmsLinkType === CmsLinkType.COOKIE_SETTINGS) {
      event.preventDefault()
    }
    switch (this.cmsLinkType$.value) {
      case CmsLinkType.DOCUMENT_LINK:
      case CmsLinkType.DOCUMENT_LINK_BUTTON: {
        this.events.dispatch(new DownloadEvent(this.data$.value.assetType ?? '', this.data$.value.assetName ?? ''))
        break
      }
      case CmsLinkType.ANCHOR:
      case CmsLinkType.ANCHOR_BUTTON: {
        this.events.dispatch(new ClickEvent('Anchors', this.data$.value.tooltip ?? '', ''))
        this.router.navigate([], { fragment: this.anchor$.value ?? '' })
        break
      }
      case CmsLinkType.CHATBOT_LINK: {
        this.chatBotService.openBot()
        break
      }
      case CmsLinkType.COOKIE_SETTINGS: {
        const window = this.winRef?.nativeWindow as Window
        if (!window) {
          return
        }
        window.UC_UI.showSecondLayer()
        break
      }
      case CmsLinkType.PRINT: {
        this.winRef.nativeWindow?.print()
        break
      }
    }
    this.clickEvent.emit(event)
  }

  ngOnInit (): void {
    if (this.winRef.nativeWindow?.Cypress) {
      let i = 0
      let name: any = 'mb_link_' + this.dataCy$.value
      while (!!this.winRef.nativeWindow[name]) {
        name = 'mb_link_' + this.dataCy$.value + `_${++i}`
      }
      (this as any).uid = 'mb_link_' + this.dataCy$.value
      this.winRef.nativeWindow[name] = this as any
    }
  }

  private retrieveProductEventUrl (): Observable<string> {
    return this.eventService.retrieve()
      .pipe(
        map(event => event?.event),
        filter(event => event instanceof ProductResolvedEvent && event?.productCode === this.data$.value.productCode))
      .pipe(
        take(1),
        map(event => (event as ProductResolvedEvent).url ?? ''),
        tap(url => this.anchor$.next(url.split('#')?.pop() ?? '')),
        map(url => url.split('#')?.shift() || '/not-found'))
  }

  protected readonly cmsLinkType = CmsLinkType
}
